import { useEffect, useRef, useState } from "react";
import { api, fileUrl, getAuthHeaders } from "../api";
import type { FileKind, RecordAttachment } from "../types";
import { Button } from "./ui/Button";
import AuthImage from "./AuthImage";
import { renderPdfThumbnail } from "../lib/pdfThumbnail";

// Presentational card for a single attachment. Used in the expanded
// record view (compact, comfortable, and reading modes all share it).
// Pulls the full extracted text lazily the first time the user
// expands the preview — keeps the initial payload from the /records
// list endpoint lean.
//
// File access (View, Download, PDF thumbnail) MUST go through an
// auth-aware blob fetch: the /images/* endpoint requires a Clerk JWT,
// and a bare `window.open(url)` or `<a href download>` cannot attach
// it, which is why attachments previously appeared in the DB but never
// rendered or downloaded.

function mimeToKind(mime: string | null): FileKind {
  if (!mime) return "other";
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("audio/")) return "audio";
  if (mime.startsWith("video/")) return "video";
  if (mime === "application/pdf") return "pdf";
  if (mime.includes("wordprocessingml")) return "docx";
  if (mime.includes("spreadsheetml")) return "xlsx";
  return "other";
}

function emojiForKind(kind: FileKind): string {
  switch (kind) {
    case "pdf":
      return "📄";
    case "docx":
      return "📝";
    case "xlsx":
      return "📊";
    case "image":
      return "🖼";
    case "audio":
      return "🎙";
    case "video":
      return "🎞";
    default:
      return "📎";
  }
}

export function formatBytes(n: number | null): string {
  if (n === null || !Number.isFinite(n)) return "—";
  if (n <= 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function firstMeaningfulLine(text: string): string {
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    if (/^\[.*\]$/.test(line)) continue;
    return line.slice(0, 220);
  }
  return "";
}

// Auth-aware blob fetch for /images/* — adds the Clerk bearer that a
// plain `<a href>` or `window.open` can't. Throws on non-2xx so the
// caller can surface an error without silently failing.
//
// `forceType`: re-label the blob's MIME. R2 stores whatever Content-Type was
// set at upload, and some attachments — notably email-intake PDFs whose mail
// client declared `application/octet-stream` (and predate filename-based
// inference, or had no `.pdf` extension) — were saved with the wrong type.
// The inline <iframe> renders a blob: URL by its `.type`, so an octet-stream
// blob can't inline-render and Chrome shows "This content is blocked." Re-typing
// to what the DB knows the file is (application/pdf for a PDF card) makes the
// viewer independent of R2's stored Content-Type, fixing existing objects with
// no re-upload.
async function fetchAttachmentBlob(
  url: string,
  forceType?: string,
): Promise<Blob> {
  const headers = await getAuthHeaders();
  const res = await fetch(url, { credentials: "include", headers });
  if (!res.ok) throw new Error(`http_${res.status}`);
  const blob = await res.blob();
  if (forceType && blob.type !== forceType) {
    return new Blob([await blob.arrayBuffer()], { type: forceType });
  }
  return blob;
}

const PDF_MIME = "application/pdf";

interface Props {
  caseId: string;
  attachment: RecordAttachment;
  onOpenLightbox?: (url: string, filename: string) => void;
  // When true (a search result deep-linked to this attachment), auto-open
  // the inline PDF viewer once on mount/when the signal flips on.
  autoExpand?: boolean;
}

export function AttachmentCard({
  caseId,
  attachment,
  onOpenLightbox,
  autoExpand,
}: Props) {
  const kind = mimeToKind(attachment.mime_type);
  const url = fileUrl(caseId, attachment.r2_key);
  const [preview, setPreview] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [pdfThumbUrl, setPdfThumbUrl] = useState<string | null>(null);
  const [acting, setActing] = useState<"open" | "download" | null>(null);
  // Inline PDF viewer: the thumbnail / View button toggles a native
  // <iframe> right inside the record panel (browser viewer = free zoom +
  // page nav). iframeFailed flips us to an open-in-new-tab fallback for
  // browsers that can't preview a PDF inline.
  const [pdfViewUrl, setPdfViewUrl] = useState<string | null>(null);
  const [pdfExpanded, setPdfExpanded] = useState(false);
  const [iframeFailed, setIframeFailed] = useState(false);
  const blobUrlRef = useRef<string | null>(null);

  // Revoke any object URL we created when the card unmounts. Object
  // URLs hold the blob in memory until revoked, and a long-lived
  // record page can otherwise accumulate dozens of MB across PDFs.
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  // Lazy-load the first line of extracted text so the card is always
  // informative without pulling 200 KB of OCR into the initial paint.
  useEffect(() => {
    let cancelled = false;
    if (!attachment.has_text) {
      setPreview(null);
      return;
    }
    setLoadingPreview(true);
    (async () => {
      try {
        const { attachment: full } = await api.getAttachment(attachment.id);
        if (cancelled) return;
        setPreview(
          full.extracted_text
            ? firstMeaningfulLine(full.extracted_text)
            : null,
        );
      } catch {
        /* preview is a nice-to-have — fail silent */
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attachment.id, attachment.has_text]);

  // PDF thumbnail: fetch the bytes through the auth-aware path then
  // render the first page to a data URL via pdfjs. The bytes ride
  // along in `blobUrlRef` too, so a follow-up "Open" doesn't re-fetch.
  useEffect(() => {
    if (kind !== "pdf") return;
    let cancelled = false;
    (async () => {
      try {
        // Force application/pdf so the cached blob URL the iframe reuses is a
        // real PDF regardless of R2's stored Content-Type.
        const blob = await fetchAttachmentBlob(url, PDF_MIME);
        if (cancelled) return;
        // Cache the blob URL for the Open button.
        if (!blobUrlRef.current) {
          blobUrlRef.current = URL.createObjectURL(blob);
        }
        const bytes = await blob.arrayBuffer();
        if (cancelled) return;
        const dataUrl = await renderPdfThumbnail(bytes, 160);
        if (!cancelled && dataUrl) setPdfThumbUrl(dataUrl);
      } catch {
        /* thumb is optional — icon stays as the fallback */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [kind, url]);

  // Deep-link from a search result: open the inline PDF viewer once when
  // the parent flips autoExpand on for this attachment.
  const autoExpandedRef = useRef(false);
  useEffect(() => {
    if (!autoExpand || autoExpandedRef.current) return;
    if (kind === "pdf" && !pdfExpanded) {
      autoExpandedRef.current = true;
      void togglePdf();
    } else if (kind === "image" && onOpenLightbox) {
      // Image attachments open in the existing lightbox rather than an
      // inline iframe (Gap 4 — same deep-link entry point as PDFs).
      autoExpandedRef.current = true;
      onOpenLightbox(url, attachment.filename);
    }
    // togglePdf is a stable closure over component state; intentionally omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoExpand, kind, pdfExpanded]);

  // Toggle the inline PDF viewer. The blob is usually already held in
  // blobUrlRef from the thumbnail effect, so expanding is a no-fetch
  // flip; we only fetch here if the thumbnail load never ran/failed.
  async function togglePdf() {
    if (pdfExpanded) {
      setPdfExpanded(false);
      return;
    }
    setIframeFailed(false);
    setActing("open");
    try {
      let blobUrl = blobUrlRef.current;
      if (!blobUrl) {
        const blob = await fetchAttachmentBlob(url, PDF_MIME);
        blobUrl = URL.createObjectURL(blob);
        blobUrlRef.current = blobUrl;
      }
      setPdfViewUrl(blobUrl);
      setPdfExpanded(true);
    } catch (err) {
      window.alert(`Couldn't open: ${(err as Error).message}`);
    } finally {
      setActing(null);
    }
  }

  // New-tab fallback, reused by the inline viewer's error state.
  async function openInNewTab() {
    try {
      let blobUrl = blobUrlRef.current;
      if (!blobUrl) {
        const blob = await fetchAttachmentBlob(url, kind === "pdf" ? PDF_MIME : undefined);
        blobUrl = URL.createObjectURL(blob);
        blobUrlRef.current = blobUrl;
      }
      window.open(blobUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      window.alert(`Couldn't open: ${(err as Error).message}`);
    }
  }

  async function onOpen() {
    if (kind === "image" && onOpenLightbox) {
      onOpenLightbox(url, attachment.filename);
      return;
    }
    if (kind === "pdf") {
      void togglePdf();
      return;
    }
    setActing("open");
    try {
      let blobUrl = blobUrlRef.current;
      if (!blobUrl) {
        const blob = await fetchAttachmentBlob(url);
        blobUrl = URL.createObjectURL(blob);
        blobUrlRef.current = blobUrl;
      }
      window.open(blobUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      window.alert(`Couldn't open: ${(err as Error).message}`);
    } finally {
      setActing(null);
    }
  }

  async function onDownload() {
    setActing("download");
    try {
      let blobUrl = blobUrlRef.current;
      if (!blobUrl) {
        const blob = await fetchAttachmentBlob(url);
        blobUrl = URL.createObjectURL(blob);
        blobUrlRef.current = blobUrl;
      }
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = attachment.filename || "download";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      window.alert(`Couldn't download: ${(err as Error).message}`);
    } finally {
      setActing(null);
    }
  }

  const sizeLabel = formatBytes(attachment.size_bytes);
  const mimeLabel = attachment.mime_type ?? "unknown type";
  const showOpen = kind !== "other";

  return (
    <div className={`attach-card attach-card-kind-${kind}`}>
      <div className="attach-card-head">
        {kind === "pdf" && pdfThumbUrl ? (
          <button
            type="button"
            className="attach-card-pdf-thumb"
            onClick={onOpen}
            aria-label={`${pdfExpanded ? "Hide" : "View"} ${attachment.filename}`}
            aria-expanded={pdfExpanded}
            title={pdfExpanded ? "Hide PDF" : "View PDF"}
          >
            <img src={pdfThumbUrl} alt="" loading="lazy" />
          </button>
        ) : (
          <span className="attach-card-icon" aria-hidden>
            {emojiForKind(kind)}
          </span>
        )}
        <div className="attach-card-meta">
          <div className="attach-card-name" title={attachment.filename}>
            {attachment.filename}
          </div>
          <div className="attach-card-sub">
            {mimeLabel}
            {sizeLabel !== "—" ? ` · ${sizeLabel}` : ""}
          </div>
        </div>
      </div>
      {kind === "pdf" && pdfExpanded && (
        <div className="attach-card-pdf-frame-wrap">
          {iframeFailed ? (
            <div className="attach-card-pdf-fallback">
              <span>This browser can't preview the PDF inline.</span>
              <Button variant="secondary" size="sm" onClick={openInNewTab}>
                Open in new tab
              </Button>
            </div>
          ) : pdfViewUrl ? (
            <>
              <iframe
                src={pdfViewUrl}
                title={attachment.filename}
                className="attach-card-pdf-frame"
                onError={() => setIframeFailed(true)}
              />
              {/* A blocked/failed PDF iframe doesn't fire onError reliably, so
                  Chrome's bare "This content is blocked" can sit here with no
                  way out. Always offer an explicit escape hatch beneath it. */}
              <div className="attach-card-pdf-escape">
                <span>Not displaying?</span>
                <button type="button" className="attach-card-pdf-escape-link" onClick={openInNewTab}>
                  Open in new tab ↗
                </button>
              </div>
            </>
          ) : null}
        </div>
      )}
      {kind === "image" && (
        <button
          type="button"
          className="attach-card-thumb-wrap"
          onClick={onOpen}
          aria-label={`Open ${attachment.filename}`}
        >
          <AuthImage
            src={url}
            alt={attachment.filename}
            className="attach-card-thumb"
            loading="lazy"
          />
        </button>
      )}
      {(preview || loadingPreview) && (
        <div className="attach-card-preview">
          {loadingPreview ? (
            <span className="muted">Loading preview…</span>
          ) : (
            <span>“{preview}”</span>
          )}
        </div>
      )}
      <div className="attach-card-actions">
        {showOpen && (
          <Button
            variant="secondary"
            size="sm"
            onClick={onOpen}
            disabled={acting === "open"}
          >
            {acting === "open"
              ? kind === "pdf"
                ? "Loading…"
                : "Opening…"
              : kind === "pdf"
                ? pdfExpanded
                  ? "Hide"
                  : "View"
                : "Open"}
          </Button>
        )}
        <Button
          variant="secondary"
          size="sm"
          onClick={onDownload}
          disabled={acting === "download"}
        >
          {acting === "download" ? "Downloading…" : "Download"}
        </Button>
      </div>
    </div>
  );
}

export function attachmentKind(mime: string | null): FileKind {
  return mimeToKind(mime);
}

export function attachmentEmoji(kind: FileKind): string {
  return emojiForKind(kind);
}
